import ky from 'ky'
import type { AuthUser } from '@common/types'

export const api = ky.create({
  prefix: '/api',
  credentials: 'include', // Send cookies with requests
  timeout: 30_000,
})

export async function fetchCurrentUser(): Promise<AuthUser> {
  return api.get('auth/me').json<AuthUser>()
}

export async function logout(): Promise<void> {
  await api.post('auth/logout')
}

export async function logoutAll(): Promise<void> {
  await api.post('auth/logout-all')
}

// User Management API

import type {
  UsersListResponse,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRoleRequest,
  UpdateUserRoleResponse,
  RoleHistoryResponse,
  ForceLogoutResponse,
  DeleteClientResponse,
  AuditorsListResponse,
  AuditorDetailResponse,
  CreateAuditorRequest,
  UpdateAuditorRequest,
  AuditorResponse,
  PublishAuditorResponse,
  ArchiveAuditorResponse,
  AuditorHistoryResponse,
  VendorReviewsListResponse,
  VendorResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  RisksListResponse,
  RiskDetailResponse,
  RiskResponse,
  ArchiveRiskResponse,
  RiskHistoryResponse,
  BulkUploadRisksToClientsResponse,
  ClientsListResponse,
  BulkUploadToClientsRequest,
  BulkUploadToClientsResponse,
  BridgeLetterResponse,
  BridgeLetterHistoryResponse,
} from '@common/types'
import type { CreateRiskInput, UpdateRiskInput, BulkUploadRisksToClientsInput } from '@common/schemas'

export async function fetchUsers(): Promise<UsersListResponse> {
  return api.get('users').json<UsersListResponse>()
}

export async function createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
  return api.post('users', { json: data }).json<CreateUserResponse>()
}

export async function updateUserRole(userId: string, data: UpdateUserRoleRequest): Promise<UpdateUserRoleResponse> {
  return api.put(`users/${userId}/role`, { json: data }).json<UpdateUserRoleResponse>()
}

export async function fetchRoleHistory(userId: string): Promise<RoleHistoryResponse> {
  return api.get(`users/${userId}/history`).json<RoleHistoryResponse>()
}

export async function forceLogoutUser(userId: string): Promise<ForceLogoutResponse> {
  return api.post(`users/${userId}/force-logout`).json<ForceLogoutResponse>()
}

// Client Management API

export async function deleteClient(clientId: string): Promise<DeleteClientResponse> {
  return api.delete(`clients/${clientId}`).json<DeleteClientResponse>()
}

// Client API

export async function fetchMinimalClients(): Promise<ClientsListResponse> {
  return api.get('clients/minimal').json<ClientsListResponse>()
}

// Vendor Management API

export async function createVendorApi(data: CreateVendorRequest): Promise<VendorResponse> {
  return api.post('vendors', { json: data }).json<VendorResponse>()
}

export async function updateVendorApi(id: string, data: UpdateVendorRequest): Promise<VendorResponse> {
  return api.put(`vendors/${id}`, { json: data }).json<VendorResponse>()
}

export async function bulkUploadVendorsToClientsApi(
  data: BulkUploadToClientsRequest
): Promise<BulkUploadToClientsResponse> {
  return api
    .post('vendors/bulk/upload-to-clients', { json: data, timeout: 120_000 })
    .json<BulkUploadToClientsResponse>()
}

// Auditor Management API

export async function fetchAuditors(): Promise<AuditorsListResponse> {
  return api.get('auditors').json<AuditorsListResponse>()
}

export async function fetchAuditor(id: string): Promise<AuditorDetailResponse> {
  return api.get(`auditors/${id}`).json<AuditorDetailResponse>()
}

export async function fetchAuditorReviews(id: string): Promise<VendorReviewsListResponse> {
  return api.get(`auditors/${id}/reviews`).json<VendorReviewsListResponse>()
}

export async function fetchAuditorHistory(id: string): Promise<AuditorHistoryResponse> {
  return api.get(`auditors/${id}/history`).json<AuditorHistoryResponse>()
}

export async function createAuditorApi(data: CreateAuditorRequest): Promise<AuditorResponse> {
  return api.post('auditors', { json: data }).json<AuditorResponse>()
}

export async function updateAuditorApi(id: string, data: UpdateAuditorRequest): Promise<AuditorResponse> {
  return api.put(`auditors/${id}`, { json: data }).json<AuditorResponse>()
}

export async function publishAuditorApi(id: string): Promise<PublishAuditorResponse> {
  return api.post(`auditors/${id}/publish`).json<PublishAuditorResponse>()
}

export async function archiveAuditorApi(id: string): Promise<ArchiveAuditorResponse> {
  return api.post(`auditors/${id}/archive`).json<ArchiveAuditorResponse>()
}

// Risk Management API

export async function fetchRisks(): Promise<RisksListResponse> {
  return api.get('risks').json<RisksListResponse>()
}

export async function fetchRisk(id: string): Promise<RiskDetailResponse> {
  return api.get(`risks/${id}`).json<RiskDetailResponse>()
}

export async function fetchRiskHistory(id: string): Promise<RiskHistoryResponse> {
  return api.get(`risks/${id}/history`).json<RiskHistoryResponse>()
}

export async function createRiskApi(data: CreateRiskInput): Promise<RiskResponse> {
  return api.post('risks', { json: data }).json<RiskResponse>()
}

export async function updateRiskApi(id: string, data: UpdateRiskInput): Promise<RiskResponse> {
  return api.put(`risks/${id}`, { json: data }).json<RiskResponse>()
}

export async function archiveRiskApi(id: string): Promise<ArchiveRiskResponse> {
  return api.post(`risks/${id}/archive`).json<ArchiveRiskResponse>()
}

export async function bulkUploadRisksToClientsApi(
  data: BulkUploadRisksToClientsInput
): Promise<BulkUploadRisksToClientsResponse> {
  return api
    .post('risks/bulk/upload-to-clients', { json: data, timeout: 120_000 })
    .json<BulkUploadRisksToClientsResponse>()
}

// Bridge Letter API

export async function uploadBridgeLetterApi(reviewId: number, file: File): Promise<BridgeLetterResponse> {
  const formData = new FormData()
  formData.append('file', file)
  // Multipart body — no `json` option so ky lets the browser set the boundary.
  return api
    .post(`reviews/${reviewId}/bridge-letters`, { body: formData, timeout: 120_000 })
    .json<BridgeLetterResponse>()
}

export async function extendBridgeLetterApi(id: string): Promise<BridgeLetterResponse> {
  return api.post(`bridge-letters/${id}/extend`).json<BridgeLetterResponse>()
}

// Archive (soft-delete) a bridge letter. After archiving, the review accepts a
// replacement upload again (the two-step replace flow).
export async function archiveBridgeLetterApi(id: string): Promise<{ success: boolean }> {
  return api.delete(`bridge-letters/${id}`).json<{ success: boolean }>()
}

// Full edit/extension history for a bridge letter. Fetched separately (like review
// history), so it isn't bundled into the review-detail payload.
export async function getBridgeLetterHistoryApi(id: string): Promise<BridgeLetterHistoryResponse> {
  return api.get(`bridge-letters/${id}/history`).json<BridgeLetterHistoryResponse>()
}
